import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import PageHero from "@/components/PageHero";
import useSEO from "@/hooks/useSEO";
import { SaaviMark, SAAVI_BRAND_LOCKUP_URL } from "@/components/SaaviLogo";
import { Lightbulb, ShieldCheck, Trophy, Sparkles, Gauge, ArrowUpRight } from "lucide-react";

const VALUES = [
  { icon: Lightbulb, label: "Innovation", desc: "Always testing, always evolving.", color: "text-saavi-blue" },
  { icon: ShieldCheck, label: "Transparency", desc: "Real numbers. Honest reports.", color: "text-saavi-red" },
  { icon: Trophy, label: "Accountability", desc: "Your KPIs are our KPIs.", color: "text-saavi-green" },
  { icon: Sparkles, label: "Creativity", desc: "Ideas that move metrics.", color: "text-saavi-blue" },
  { icon: Gauge, label: "Performance", desc: "ROI over impressions, always.", color: "text-saavi-red" },
];

export default function About() {
  useSEO();
  return (
    <>
      <PageHero
        eyebrow="About Saavi Digitex"
        title={<>We Build Digital <span className="text-saavi-blue">Growth Engines.</span></>}
        description="At Saavi Digitex, we help brands attract, engage, and convert their ideal customers through strategic digital marketing solutions."
      />

      <section className="section-pad border-b border-border">
        <div className="container-saavi grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-6 text-base md:text-lg text-muted-foreground leading-relaxed">
            <div className="hidden lg:flex items-center gap-4 mb-2">
              <SaaviMark size={96} />
              <div className="flex flex-col gap-1">
                <span className="font-mono-overline text-saavi-blue">Three pillars · One growth engine</span>
              </div>
            </div>
            <p>
              With expertise across <span className="text-foreground font-semibold">Digital Marketing, Performance Marketing, Branding, Social Media, Content Creation, SEO, Lead Generation, and Marketing Automation</span>, we help businesses achieve sustainable growth while maximizing ROI.
            </p>
            <p>
              Our approach combines creativity, technology, and analytics to ensure every marketing effort contributes directly to business growth — not vanity metrics.
            </p>
          </div>

          <div className="lg:col-span-5 grid grid-cols-1 gap-4">
            <div className="border border-border rounded-md p-8 bg-card border-l-4 border-l-saavi-blue">
              <div className="font-mono-overline text-saavi-blue">Our Mission</div>
              <p className="mt-3 text-lg font-semibold leading-snug">
                To help businesses leverage digital marketing as a predictable revenue-generating channel.
              </p>
            </div>
            <div className="border border-border rounded-md p-8 bg-card border-l-4 border-l-saavi-red">
              <div className="font-mono-overline text-saavi-red">Our Vision</div>
              <p className="mt-3 text-lg font-semibold leading-snug">
                To become one of India's most trusted growth marketing partners for ambitious brands.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad border-b border-border">
        <div className="container-saavi">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="font-mono-overline text-saavi-blue mb-4">Our Brand Identity</div>
              <h2 className="text-4xl md:text-5xl">Three colors. Three growth pillars.</h2>
              <p className="mt-5 text-muted-foreground leading-relaxed max-w-xl">
                Our mark isn't just a logo — it's our growth philosophy. The three intersecting peaks represent the disciplines we combine in every engagement.
              </p>
              <ul className="mt-8 space-y-4">
                <li className="flex items-start gap-4">
                  <span className="mt-1.5 inline-block h-3 w-3 rounded-sm bg-saavi-red flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-base">Performance — the Red peak</div>
                    <p className="text-sm text-muted-foreground">Paid acquisition, conversion optimization and ROI-focused campaigns.</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="mt-1.5 inline-block h-3 w-3 rounded-sm bg-saavi-blue flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-base">Brand — the Blue peak</div>
                    <p className="text-sm text-muted-foreground">Identity, content, and storytelling that builds trust and authority.</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="mt-1.5 inline-block h-3 w-3 rounded-sm bg-saavi-green flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-base">Growth — the Green peak</div>
                    <p className="text-sm text-muted-foreground">Lead generation, automation and the compounding systems that scale revenue.</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2">
              <div data-testid="brand-lockup-card" className="relative bg-white rounded-md border border-border overflow-hidden shadow-sm">
                <div className="absolute inset-x-0 top-0 flex items-center justify-between px-4 py-2 border-b border-zinc-200 text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500 bg-zinc-50">
                  <span>Saavi Digitex · Official Mark</span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#E30613" }} />
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#1F4E9E" }} />
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#2BA84A" }} />
                  </span>
                </div>
                <img
                  src={SAAVI_BRAND_LOCKUP_URL}
                  alt="Saavi Digitex official brand lockup"
                  className="w-full h-auto block pt-10 pb-6 px-8 md:px-14"
                  loading="lazy"
                />
                <div className="border-t border-zinc-200 px-4 py-2 text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500 bg-zinc-50 flex items-center justify-between">
                  <span>Primary Lockup · v1.0</span>
                  <span>1:1 · CMYK / RGB</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad border-b border-border">
        <div className="container-saavi">
          <div className="font-mono-overline text-saavi-green mb-4">Core Values</div>
          <h2 className="text-4xl md:text-5xl max-w-2xl">Principles that guide every campaign we run.</h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
            {VALUES.map(({ icon: Icon, label, desc, color }) => (
              <div key={label} className="card-hover border border-border rounded-md p-6 bg-card">
                <Icon className={`h-6 w-6 ${color}`} />
                <h3 className="mt-5 text-xl">{label}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-saavi flex flex-col md:flex-row items-start md:items-center justify-between gap-6 border border-border rounded-md p-10 bg-card">
          <div>
            <h3 className="text-2xl md:text-3xl">Want to know how we'd grow your brand?</h3>
            <p className="mt-2 text-muted-foreground">A short call is all it takes.</p>
          </div>
          <Link to="/contact"><Button size="lg" className="h-12 px-6 gap-2">Talk to Us <ArrowUpRight className="h-4 w-4" /></Button></Link>
        </div>
      </section>
    </>
  );
}
